import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Shield, LogOut } from "lucide-react";
import { COLORS, SANS, HEADER } from "../dispute/theme";
import { useIsMobile, merge } from "../../hooks/useIsMobile";
import Logo from "../Logo";
import AdminStripeModeBanner from "./AdminStripeModeBanner";

const TABS = [
  { key: "disputes", label: "Disputes", path: "/admin" },
  { key: "users", label: "Users", path: "/admin/users" },
  { key: "config", label: "Config", path: "/admin/config" },
  { key: "prompts", label: "Prompts", path: "/admin/prompts" },
];

// Shared sticky header for the authenticated admin console (AdminDisputes,
// AdminUsers, AdminUserEdit, AdminUserStripe, AdminConfig, AdminPrompts).
// `active` highlights the current top-level tab (omit on drill-down pages
// like AdminUserEdit/AdminUserStripe, which sit under Users but aren't it).
export default function AdminHeader({ active, onLogout, showNewDispute = true }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!isMobile && navOpen) setNavOpen(false);
  }, [isMobile, navOpen]);

  const env = process.env.REACT_APP_ENV || "local";

  return (
    <header style={styles.header}>
      <div style={merge(styles.headerInner, styles.headerInnerMobile, isMobile)}>
        <Logo onClick={() => navigate("/home")} />

        {!isMobile && (
          <>
            <span style={styles.adminBadge}>
              <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
              ADMIN
            </span>
            {env !== "production" && <span style={styles.envBadge}>{env}</span>}
            <nav style={styles.navLinks}>
              {TABS.map((t) =>
                t.key === active ? (
                  <span key={t.key} style={{ ...styles.navLink, ...styles.navLinkActive }}>{t.label}</span>
                ) : (
                  <button key={t.key} onClick={() => navigate(t.path)} style={styles.navLink}>{t.label}</button>
                )
              )}
            </nav>
            <div style={{ flex: 1 }} />
            {showNewDispute && (
              <button onClick={() => navigate("/submit-dispute")} style={styles.navNewBtn}>+ New Dispute</button>
            )}
            <button onClick={onLogout} style={styles.logoutBtn}>
              <LogOut size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Sign out (admin)
            </button>
          </>
        )}

        {isMobile && (
          <button
            onClick={() => setNavOpen(!navOpen)}
            aria-label={navOpen ? "Close menu" : "Open menu"}
            style={styles.burgerBtn}
          >
            {navOpen ? <X size={22} strokeWidth={1.75} /> : <Menu size={22} strokeWidth={1.75} />}
          </button>
        )}
      </div>

      {isMobile && navOpen && (
        <div style={styles.mobileDrawer}>
          <span style={styles.adminBadge}>ADMIN</span>
          {env !== "production" && <span style={styles.envBadge}>{env}</span>}
          {TABS.filter((t) => t.key !== active).map((t) => (
            <button
              key={t.key}
              onClick={() => { setNavOpen(false); navigate(t.path); }}
              style={styles.mobileDrawerCta}
            >
              {t.label}
            </button>
          ))}
          {showNewDispute && (
            <button
              onClick={() => { setNavOpen(false); navigate("/submit-dispute"); }}
              style={styles.mobileDrawerCta}
            >
              + New Dispute
            </button>
          )}
          <button
            onClick={() => { setNavOpen(false); onLogout(); }}
            style={styles.mobileDrawerCta}
          >
            Sign out (admin)
          </button>
        </div>
      )}

      {/* Which Stripe keys the site is on. Admin console only — see the
          component for why it renders in live mode too. */}
      <AdminStripeModeBanner />
    </header>
  );
}

const styles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: HEADER.zIndex,
    background: HEADER.background,
    backdropFilter: HEADER.backdropFilter,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  headerInner: { display: "flex", alignItems: "center", gap: 14, padding: HEADER.paddingDesktop },
  headerInnerMobile: { padding: HEADER.paddingMobile, gap: 10 },
  adminBadge: {
    background: COLORS.dark, color: "#fff", borderRadius: 4,
    padding: "3px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
    textTransform: "uppercase",
  },
  envBadge: {
    background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4,
    padding: "2px 8px", fontSize: 11, color: COLORS.inkMuted, textTransform: "uppercase", letterSpacing: 1,
  },
  navLinks: { display: "flex", gap: 4, alignItems: "center" },
  navLink: {
    background: "transparent", border: "none", borderRadius: 6,
    padding: "6px 12px", fontSize: 13, fontWeight: 600, fontFamily: SANS,
    color: COLORS.inkSoft, cursor: "pointer",
  },
  navLinkActive: { color: COLORS.ink, background: COLORS.bgAlt, cursor: "default" },
  navNewBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 6,
    padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600, fontFamily: SANS,
  },
  logoutBtn: {
    background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6,
    padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600, fontFamily: SANS, color: COLORS.ink,
  },
  burgerBtn: {
    marginLeft: "auto", background: "transparent", border: "none", color: COLORS.ink,
    cursor: "pointer", padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center",
  },
  mobileDrawer: {
    borderTop: `1px solid ${COLORS.border}`, background: COLORS.bg,
    padding: "14px 20px 18px 20px", display: "flex", flexDirection: "column", gap: 10, marginTop: 12,
  },
  mobileDrawerCta: {
    background: COLORS.dark, color: "#fff", border: "none", padding: "12px 16px",
    fontSize: 14, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: SANS, width: "100%",
  },
};
